import { jsx } from '@emotion/react/jsx-runtime';
import { useTheme } from '@emotion/react';
import { createPolymorphicComponent, useSlot, useShouldInvertColors, useAdaptiveHeightContext, useBleed, ThemeProvider, AdaptiveColorProvider, AdaptiveHeightProvider, SlotProvider } from '@nettbureau/core';
import { useCss, inset } from '@nettbureau/styling';
import { useShapeProps } from './use-shape-props.js';

/**
 * Shape is a primary building block in our component library. It can be tailored using theme tokens
 * for precise customization. It comes in a variety of shapes and sizes, and can be used to create
 * buttons, cards, and more.
 */ const Shape = createPolymorphicComponent((props, ref)=>{
    const [{ as, css, ...mergedProps }, mergedRef] = useSlot('Shape', props, ref);
    const { bleed, borderRadius, children, fill, fillTone, height, heightToken, figure, layout, margin, maxHeight, maxWidth, minHeight, minWidth, outline, outlineTone, outlineWidth, padding, shadow, typography, width, ...elementProps } = useShapeProps(mergedProps);
    const theme = useTheme();
    const shouldInvertColor = useShouldInvertColors(fill, fillTone);
    const adaptiveHeight = heightToken ? theme.maps.heightToContentHeight[heightToken] : null;
    const adaptiveColor = fill !== 'base' ? fill : null;
    const bleedArea = useAdaptiveHeightContext();
    const [bleedX, bleedY] = useBleed(bleed, {
        x: heightToken && -theme.spacings[theme.maps.heightToSpacing[heightToken]],
        y: heightToken && bleedArea && (theme.heights[bleedArea] - theme.heights[heightToken]) / 2
    });
    const shapeCss = useCss(({ mixins })=>[
            mixins.borderRadius({
                radius: borderRadius
            }),
            mixins.fill({
                fill,
                fillTone
            }),
            mixins.outline({
                outline,
                outlineTone,
                outlineWidth
            }),
            mixins.spacing({
                margin,
                padding
            }),
            mixins.size({
                height,
                maxHeight,
                maxWidth,
                minHeight,
                minWidth,
                width
            }),
            mixins.transition({
                properties: [
                    'background',
                    'border',
                    'borderRadius',
                    'boxShadow',
                    'color',
                    'outline',
                    'padding',
                    'transform'
                ]
            }),
            mixins.zIndex(0),
            layout && mixins.flex(layout),
            bleedX && {
                '[data-shape] > &:first-child': {
                    marginLeft: bleedX
                },
                '[data-shape] > &:last-child': {
                    marginRight: bleedX
                }
            },
            bleedY && {
                marginTop: bleedY,
                marginBottom: bleedY
            },
            {
                // Enabling hardware acceleration for smoother animations.
                transform: 'translate3d(0, 0, 0)',
                position: 'relative',
                '&::before': [
                    inset(0),
                    mixins.borderRadius({
                        radius: borderRadius
                    }),
                    mixins.shadow(shadow),
                    mixins.zIndex(-1),
                    {
                        content: '""',
                        pointerEvents: 'none',
                        position: 'absolute'
                    }
                ]
            }
        ]);
    const overrides = {
        Divider: layout && {
            direction: layout.direction === 'vertical' ? 'horizontal' : 'vertical'
        },
        Shape: {
            bleed: !!heightToken
        }
    };
    return /*#__PURE__*/ jsx(ThemeProvider, {
        colorInverted: shouldInvertColor,
        children: /*#__PURE__*/ jsx(AdaptiveColorProvider, {
            value: adaptiveColor,
            children: /*#__PURE__*/ jsx(AdaptiveHeightProvider, {
                value: adaptiveHeight,
                children: /*#__PURE__*/ jsx(SlotProvider, {
                    value: overrides,
                    children: /*#__PURE__*/ jsx(ShapeContent, {
                        "data-shape": true,
                        "data-fill": fill,
                        "data-fill-tone": fillTone,
                        ...elementProps,
                        ref: mergedRef,
                        as: as,
                        css: [
                            shapeCss,
                            css
                        ],
                        typography: typography,
                        children: children
                    })
                })
            })
        })
    });
});
/**
 * This is an internal component that wraps the content of the shape and applies typography styles.
 * This is a separate component so that we can apply typography styles using the theme set by the
 * shape itself.
 */ const ShapeContent = createPolymorphicComponent((props, ref)=>{
    const { as: Component = 'div', children, typography, ...elementProps } = props;
    const css = useCss(({ mixins })=>typography && mixins.typography(typography));
    return /*#__PURE__*/ jsx(Component, {
        ...elementProps,
        ref: ref,
        css: css,
        children: children
    });
});

export { Shape, ShapeContent };
//# sourceMappingURL=shape.js.map
